import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import PagamentiDialog from '@/components/PagamentiDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, AlertTriangle, CheckCircle, Clock, Upload, Eye, FileText, Download, Pencil, Lock, Unlock, History, Send, BookCheck, CreditCard, ChevronDown, ChevronRight, LayoutList, ListTree, Building2, Copy } from 'lucide-react';
import { format, differenceInDays, parseISO, isValid } from 'date-fns';
import { toast } from 'sonner';
import { getFileUrl } from '@/utils/fileUrl';
import { FilePreview } from '@/components/FilePreview';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { BulkActionToolbar } from '@/components/BulkActionToolbar';
import { AccontoSelector } from '@/components/AccontoSelector';
import ImportXmlBulkDialog from '@/components/ImportXmlBulkDialog';
import { SafeTableBoundary } from '@/components/SafeTableBoundary';
import { CantiereSelectByCliente } from '@/components/CantiereSelectByCliente';
import { AndamentoMensileFatture } from '@/components/AndamentoMensileFatture';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FattureEntrataPage({ forceTipo, title, subtitle }) {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const [fatture, setFatture] = useState([]);
  // iter46: lista completa NON filtrata, usata dal dropdown "Fattura di riferimento"
  // della nota di credito (deve poter selezionare anche fatture normali quando
  // siamo nella pagina dedicata alle note di credito con forceTipo='nota_credito').
  const [tutteFatture, setTutteFatture] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [logDoc, setLogDoc] = useState(null);
  // Bulk selection (must be declared before any early-return to satisfy rules-of-hooks)
  const bulkSel = useBulkSelection(fatture, (f) => f.id);
  const initialTipo = forceTipo || 'fattura';
  const [form, setForm] = useState({ fornitore: '', fornitore_piva: '', fornitore_id: '', numero: '', data: new Date().toISOString().split('T')[0], scadenza: '', importo: '', imponibile: '', iva: '', aliquota_iva: '22', input_mode: 'imponibile', descrizione: '', cantiere_id: '', tipo_documento: initialTipo, riferimento_fattura_id: '', file_url: '', modalita_pagamento_id: '', pagamento_formula: '', acconto_riferimento: '' });
  const [fornitoriList, setFornitoriList] = useState([]);
  const [modalitaList, setModalitaList] = useState([]);
  // iter81: import XML in massa
  const [bulkXmlOpen, setBulkXmlOpen] = useState(false);
  // iter100o: dedupe
  const [dedupeBusy, setDedupeBusy] = useState(false);

  const checkAndDedupe = async () => {
    setDedupeBusy(true);
    try {
      const r = await axios.get(`${API}/fatture-entrata/check-duplicates`, { withCredentials: true });
      const { total_groups, total_extra_copies, groups } = r.data;
      if (total_extra_copies === 0) {
        toast.success('Nessun duplicato trovato');
        return;
      }
      const preview = groups.slice(0, 5).map(g => `• N.${g.numero} del ${g.data} (${g.fornitore}) — ${g.count} copie`).join('\n');
      const ok = window.confirm(
        `Trovate ${total_extra_copies} copie duplicate in ${total_groups} gruppi.\n\n` +
        `${preview}${groups.length > 5 ? `\n…e altre ${groups.length - 5}` : ''}\n\n` +
        `Vuoi rimuovere le copie extra?\n(Il record più vecchio di ogni gruppo viene mantenuto)`
      );
      if (!ok) { toast.info('Nessuna modifica'); return; }
      const r2 = await axios.post(`${API}/fatture-entrata/dedupe?dry_run=false`, {}, { withCredentials: true });
      toast.success(`${r2.data.deleted_count || 0} copie duplicate rimosse`);
      fetchFatture();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore verifica duplicati');
    } finally {
      setDedupeBusy(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/fornitori`, { withCredentials: true })
      .then(r => setFornitoriList(r.data || []))
      .catch(() => {});
    axios.get(`${API}/modalita-pagamento`, { withCredentials: true })
      .then(r => setModalitaList(r.data || []))
      .catch(() => {});
  }, []);

  // Auto-calcolo scadenza dalla modalità di pagamento
  const recalcScadenza = async (override = {}) => {
    const dataRif = override.data || form.data;
    if (!dataRif) return;
    const modalita_id = override.modalita_pagamento_id ?? form.modalita_pagamento_id;
    if (!modalita_id) return;
    try {
      const r = await axios.post(`${API}/modalita-pagamento/calcola`, {
        data_fattura: dataRif,
        modalita_id,
      }, { withCredentials: true });
      if (r.data?.scadenza) {
        setForm(prev => ({
          ...prev,
          scadenza: r.data.scadenza,
          pagamento_formula: r.data.formula_applicata || '',
          modalita_pagamento_id: modalita_id,
        }));
      }
    } catch { /* silent */ }
  };

  // Auto-calc helper: recomputes imponibile/iva/importo based on input_mode and aliquota
  const recalc = (next) => {
    const aliquota = parseFloat(next.aliquota_iva || 22) || 0;
    const mode = next.input_mode || 'imponibile';
    if (mode === 'imponibile') {
      const imp = parseFloat(next.imponibile || 0) || 0;
      const iva = Math.round(imp * aliquota) / 100;
      const tot = Math.round((imp * (100 + aliquota))) / 100;
      return { ...next, iva: iva ? iva.toFixed(2) : '', importo: tot ? tot.toFixed(2) : '' };
    } else {
      // mode === 'totale' → scorporo
      const tot = parseFloat(next.importo || 0) || 0;
      const imp = aliquota > 0 ? Math.round((tot * 100 / (100 + aliquota)) * 100) / 100 : tot;
      const iva = Math.round((tot - imp) * 100) / 100;
      return { ...next, imponibile: imp ? imp.toFixed(2) : '', iva: iva ? iva.toFixed(2) : '' };
    }
  };

  const [editingId, setEditingId] = useState(null);

  const openEdit = (f) => {
    setEditingId(f.id);
    const impVal = parseFloat(f.imponibile ?? 0) || 0;
    const totVal = parseFloat(f.importo ?? 0) || 0;
    // Pick the right input mode: if imponibile is missing/zero but totale exists, use 'totale' so recalc derives back from total
    const mode = (impVal <= 0 && totVal > 0) ? 'totale' : 'imponibile';
    setForm({
      fornitore: f.fornitore || '',
      fornitore_piva: f.fornitore_piva || '',
      fornitore_id: f.fornitore_id || '',
      numero: f.numero || '',
      data: f.data || '',
      scadenza: f.scadenza || '',
      importo: String(f.importo ?? ''),
      imponibile: String(f.imponibile ?? ''),
      iva: String(f.iva ?? ''),
      aliquota_iva: String(f.aliquota_iva ?? 22),
      input_mode: mode,
      descrizione: f.descrizione || '',
      cantiere_id: f.cantiere_id || '',
      tipo_documento: f.tipo_documento || 'fattura',
      riferimento_fattura_id: f.riferimento_fattura_id || '',
      file_url: f.file_url || '',
      modalita_pagamento_id: f.modalita_pagamento_id || '',
      pagamento_formula: f.pagamento_formula || '',
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ fornitore: '', numero: '', data: new Date().toISOString().split('T')[0], scadenza: '', importo: '', imponibile: '', iva: '', aliquota_iva: '22', input_mode: 'imponibile', descrizione: '', cantiere_id: '', tipo_documento: forceTipo || 'fattura', riferimento_fattura_id: '', file_url: '', modalita_pagamento_id: '', pagamento_formula: '', acconto_riferimento: '' });
    setDialogOpen(true);
  };

  const openLog = async (f) => {
    try {
      const res = await axios.get(`${API}/fatturazione/fatture_entrata/${f.id}/log`, { withCredentials: true });
      setLogEntries(res.data.log || []);
      setLogDoc({ protocollo: res.data.protocollo, numero: res.data.numero });
      setLogOpen(true);
    } catch { toast.error('Errore caricamento cronologia'); }
  };

  const lockSdi = async (f) => {
    if (!window.confirm(`Vuoi marcare il documento ${f.protocollo || f.numero} come INVIATO A SDI?\n\nATTENZIONE: una volta bloccato, il documento NON sarà più modificabile né eliminabile. Per rettificarlo dovrai emettere una nota di credito.`)) return;
    try {
      await axios.post(`${API}/fatturazione/fatture_entrata/${f.id}/sdi-lock`, {}, { withCredentials: true });
      toast.success('Documento bloccato e marcato come inviato a SDI');
      fetchFatture();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // Sblocca una fattura inviata a SDI per consentirne la modifica (solo super-admin)
  const unlockSdi = async (f) => {
    if (!window.confirm(`Sbloccare il documento ${f.protocollo || f.numero} per modificarlo?\n\nÈ stato inviato a SDI: modificalo solo se strettamente necessario. Lo sblocco verrà registrato nello storico.`)) return;
    try {
      await axios.post(`${API}/fatturazione/fatture_entrata/${f.id}/sdi-unlock`, {}, { withCredentials: true });
      toast.success('Documento sbloccato: ora è modificabile');
      fetchFatture();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const toggleContabilizza = async (f) => {
    try {
      await axios.post(`${API}/fatturazione/fatture_entrata/${f.id}/contabilizza`, {}, { withCredentials: true });
      toast.success('Stato contabilizzazione aggiornato');
      fetchFatture();
    } catch { toast.error('Errore'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const filePath = res.data?.path;
      if (!filePath) throw new Error('Upload fallito');
      setForm(prev => ({ ...prev, file_url: filePath }));
      toast.success('File caricato');
    } catch (err) {
      toast.error('Errore nel caricamento del file');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  // Auto-fill form when a reference invoice is selected for a credit note
  const handleSelectRiferimento = (fid) => {
    if (!fid) { setForm(prev => ({...prev, riferimento_fattura_id: ''})); return; }
    // iter46: cerca in `tutteFatture` (non in `fatture` filtrato) per supportare anche
    // la pagina "Note di Credito Ricevute" che ha forceTipo='nota_credito'.
    const ref = tutteFatture.find(f => f.id === fid) || fatture.find(f => f.id === fid);
    if (!ref) { setForm(prev => ({...prev, riferimento_fattura_id: fid})); return; }
    setForm(prev => ({
      ...prev,
      riferimento_fattura_id: fid,
      fornitore: ref.fornitore || prev.fornitore,
      importo: ref.importo ?? prev.importo,
      imponibile: ref.imponibile ?? prev.imponibile,
      iva: ref.iva ?? prev.iva,
      cantiere_id: ref.cantiere_id || prev.cantiere_id,
      descrizione: `Storno fattura ${ref.numero} - ${prev.descrizione || ''}`.trim(),
    }));
  };

  useEffect(() => { fetchFatture(); fetchCantieri(); }, [forceTipo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCantieri = async () => {
    try {
      const res = await axios.get(`${API}/cantieri`, { withCredentials: true });
      setCantieri(res.data);
    } catch (err) {
      console.error('Errore caricamento cantieri (link fattura):', err);
    }
  };

  const fetchFatture = async () => {
    try {
      const res = await axios.get(`${API}/fatture-entrata`, { withCredentials: true });
      const all = res.data || [];
      setTutteFatture(all);  // sempre completa, per il dropdown nota di credito
      // iter83-fix2: filtro robusto — trattiamo come "fattura" qualsiasi cosa NON sia
      // esattamente 'nota_credito' (copre valori legacy/spuri come 'Fattura', 'F',
      // 'fatt', null, undefined ecc). Prima il match era esatto ("=== forceTipo")
      // e nascondeva le fatture con tipo_documento anomalo.
      let filtered = all;
      if (forceTipo === 'nota_credito') {
        filtered = all.filter(f => f.tipo_documento === 'nota_credito');
      } else if (forceTipo === 'fattura') {
        filtered = all.filter(f => f.tipo_documento !== 'nota_credito');
      }
      setFatture(filtered);
    } catch (err) {
      console.error('Errore caricamento fatture entrata:', err);
      toast.error('Errore');
    }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const computed = recalc(form);
      const payload = {
        ...computed,
        importo: parseFloat(computed.importo || 0),
        imponibile: parseFloat(computed.imponibile || 0),
        iva: parseFloat(computed.iva || 0),
        aliquota_iva: parseFloat(computed.aliquota_iva || 22),
      };
      if (!payload.importo) { toast.error('Importo obbligatorio'); return; }
      if (editingId) {
        await axios.put(`${API}/fatture-entrata/${editingId}`, payload, { withCredentials: true });
        toast.success('Fattura aggiornata');
      } else {
        await axios.post(`${API}/fatture-entrata`, payload, { withCredentials: true });
        toast.success('Fattura aggiunta');
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ fornitore: '', numero: '', data: new Date().toISOString().split('T')[0], scadenza: '', importo: '', imponibile: '', iva: '', aliquota_iva: '22', input_mode: 'imponibile', descrizione: '', cantiere_id: '', tipo_documento: 'fattura', riferimento_fattura_id: '', file_url: '' });
      fetchFatture();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleChangeStato = async (id, stato) => {
    try {
      await axios.put(`${API}/fatture-entrata/${id}/stato?stato=${stato}`, {}, { withCredentials: true });
      fetchFatture();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/fatture-entrata/${id}`, { withCredentials: true }); toast.success('Eliminata'); fetchFatture(); } catch { toast.error('Errore'); }
  };

  const handleUploadXml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(xml|html|htm|p7m)$/)) {
      toast.error('Seleziona un file XML, HTML o P7M');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/fatture-entrata/import-xml`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.already_exists) {
        toast.info(`Fattura ${res.data.numero} del ${res.data.data} già presente — nessuna copia creata`);
      } else {
        toast.success(`Fattura ${res.data.numero} importata da XML`);
      }
      fetchFatture();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel parsing XML');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // iter83-fix: formattazione data resiliente a valori vuoti/malformati (una
  // singola fattura senza data non deve crashare l'intera lista).
  const fmtData = (v) => {
    if (!v) return '—';
    try {
      const d = parseISO(v);
      return isValid(d) ? format(d, 'dd-MM-yyyy') : v;
    } catch { return v || '—'; }
  };

  const getScadenzaBadge = (f) => {
    if (f.stato === 'stornata') return <Badge className="bg-slate-200 text-slate-700 border-slate-300" variant="outline"><AlertTriangle className="h-3 w-3 mr-1" />Stornata</Badge>;
    if (f.stato === 'pagata') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline"><CheckCircle className="h-3 w-3 mr-1" />Pagata</Badge>;
    if (!f.scadenza) return <Badge className="bg-slate-100 text-slate-500 border-slate-200" variant="outline">Scadenza non impostata</Badge>;
    const parsedScad = parseISO(f.scadenza);
    if (!isValid(parsedScad)) return <Badge className="bg-slate-100 text-slate-500 border-slate-200" variant="outline">Scadenza non valida</Badge>;
    const days = differenceInDays(parsedScad, new Date());
    if (days < 0) return <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline"><AlertTriangle className="h-3 w-3 mr-1" />Scaduta ({Math.abs(days)}gg)</Badge>;
    if (days <= 7) return <Badge className="bg-amber-100 text-amber-700 border-amber-200" variant="outline"><Clock className="h-3 w-3 mr-1" />Scade tra {days}gg</Badge>;
    return <Badge className="bg-slate-100 text-slate-700 border-slate-200" variant="outline">{days}gg alla scadenza</Badge>;
  };

  const totals = useMemo(() => {
    let daPagare = 0, daPagareCount = 0, pagate = 0, pagateCount = 0;
    for (const f of fatture) {
      if (f.stato === 'da_pagare') { daPagare += f.importo || 0; daPagareCount++; }
      else if (f.stato === 'pagata') { pagate += f.importo || 0; pagateCount++; }
    }
    return { daPagare, daPagareCount, pagate, pagateCount };
  }, [fatture]);
  const totDaPagare = totals.daPagare;
  const totPagate = totals.pagate;
  const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

  // Pure helper: returns the right CSS classes for the stato select pill
  const statoSelectClass = (stato) => {
    if (stato === 'pagata') return 'bg-emerald-100 text-emerald-700';
    if (stato === 'parzialmente_pagata') return 'bg-blue-100 text-blue-700';
    if (stato === 'scaduta') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  // iter47: pagamenti parziali
  const [pagamentiOpen, setPagamentiOpen] = useState(false);
  const [pagamentiFattura, setPagamentiFattura] = useState(null);
  const openPagamenti = (f) => { setPagamentiFattura(f); setPagamentiOpen(true); };

  // iter85: link cantiere anche quando SDI-locked
  // iter86-k: aggiunto acconto_riferimento nel dialog link cantiere
  const [linkCantiereFattura, setLinkCantiereFattura] = useState(null);
  const [linkCantiereValue, setLinkCantiereValue] = useState('');
  const [linkAccontoValue, setLinkAccontoValue] = useState('');
  const openLinkCantiere = (f) => {
    setLinkCantiereFattura(f);
    setLinkCantiereValue(f.cantiere_id || '');
    setLinkAccontoValue(f.acconto_riferimento || '');
  };
  const saveLinkCantiere = async () => {
    if (!linkCantiereFattura) return;
    try {
      const params = new URLSearchParams({
        cantiere_id: linkCantiereValue || '',
        acconto_riferimento: linkCantiereValue ? (linkAccontoValue || '') : '',
      });
      await axios.post(
        `${API}/fatture-entrata/${linkCantiereFattura.id}/link-cantiere?${params.toString()}`,
        {},
        { withCredentials: true }
      );
      toast.success('Cantiere aggiornato');
      setLinkCantiereFattura(null);
      fetchFatture();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento cantiere');
    }
  };

  // iter83-fix: ricerca testuale per numero/fornitore/protocollo (utile quando ci
  // sono tante fatture e serve trovarne una specifica senza scrollare).
  const location = useLocation();
  const initialQ = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('q') || '';
  }, [location.search]);
  const [searchQ, setSearchQ] = useState(initialQ);
  useEffect(() => { setSearchQ(initialQ); }, [initialQ]);

  // iter84: filtro mese + raggruppamento per mese (richiesta utente).
  // filtroMese: 'tutti' oppure 'YYYY-MM'.
  const [filtroMese, setFiltroMese] = useState('tutti');
  // iter86-q: filtro rapido per stato pagamento — 'tutte' | 'pagate' | 'da_pagare' | 'scadute'
  const [filtroStato, setFiltroStato] = useState('tutte');
  const [raggruppaMese, setRaggruppaMese] = useState(() => {
    try { return localStorage.getItem('fatture_entrata_raggruppa_mese') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('fatture_entrata_raggruppa_mese', raggruppaMese ? '1' : '0'); }
    catch { /* ignore */ }
  }, [raggruppaMese]);
  const [mesiCollassati, setMesiCollassati] = useState(new Set());
  const toggleMese = (key) => {
    setMesiCollassati(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Helper: estrai YYYY-MM da f.data in modo robusto (senza crashare).
  const meseKeyOf = (f) => {
    const d = f?.data;
    if (typeof d === 'string' && /^\d{4}-\d{2}/.test(d)) return d.slice(0, 7);
    return 'senza-data';
  };
  const meseLabel = (key) => {
    if (key === 'senza-data') return 'Senza data';
    const [y, m] = key.split('-');
    const names = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
    const idx = parseInt(m, 10) - 1;
    return `${names[idx] || m} ${y}`;
  };

  // Lista mesi disponibili (per il select), ordinata DESC (più recente prima).
  const mesiDisponibili = useMemo(() => {
    const set = new Set();
    for (const f of fatture) set.add(meseKeyOf(f));
    return Array.from(set).sort((a, b) => {
      if (a === 'senza-data') return 1;
      if (b === 'senza-data') return -1;
      return b.localeCompare(a);
    });
  }, [fatture]);

  // Applica ricerca + filtro mese.
  const fattureVisibili = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    // Coercizione a stringa DIFENSIVA per evitare crash se il campo è
    // number/null/undefined (in produzione capitano `numero` numerici).
    const safeIncludes = (v, needle) => String(v == null ? '' : v).toLowerCase().includes(needle);
    let list = fatture;
    if (q) {
      list = list.filter(f => (
        safeIncludes(f.numero, q) ||
        safeIncludes(f.fornitore, q) ||
        safeIncludes(f.protocollo, q) ||
        safeIncludes(f.descrizione, q)
      ));
    }
    if (filtroMese && filtroMese !== 'tutti') {
      list = list.filter(f => meseKeyOf(f) === filtroMese);
    }
    // iter86-q: filtro per stato pagamento
    if (filtroStato && filtroStato !== 'tutte') {
      const isScaduta = (f) => {
        if (f.stato === 'pagata' || f.stato === 'stornata') return false;
        if (f.stato === 'scaduta') return true;
        if (!f.scadenza || typeof f.scadenza !== 'string') return false;
        try {
          const parsed = parseISO(f.scadenza);
          if (!isValid(parsed)) return false;
          return differenceInDays(parsed, new Date()) < 0;
        } catch { return false; }
      };
      if (filtroStato === 'pagate') {
        list = list.filter(f => f.stato === 'pagata');
      } else if (filtroStato === 'da_pagare') {
        list = list.filter(f => (f.stato === 'da_pagare' || f.stato === 'parzialmente_pagata') && !isScaduta(f));
      } else if (filtroStato === 'scadute') {
        list = list.filter(f => isScaduta(f));
      }
    }
    return list;
  }, [fatture, searchQ, filtroMese, filtroStato]);

  // iter86-q: conteggi per i badge dei filtri stato (calcolati SU tutte le fatture,
  // così anche cambiando filtro l'utente vede sempre "quante di ogni tipo esistono").
  // iter86-u: aggiunti anche i TOTALI in euro per ogni gruppo.
  const contatoriStato = useMemo(() => {
    const isScaduta = (f) => {
      if (f.stato === 'pagata' || f.stato === 'stornata') return false;
      if (f.stato === 'scaduta') return true;
      if (!f.scadenza || typeof f.scadenza !== 'string') return false;
      try {
        const parsed = parseISO(f.scadenza);
        if (!isValid(parsed)) return false;
        return differenceInDays(parsed, new Date()) < 0;
      } catch { return false; }
    };
    let pagate = 0, daPagare = 0, scadute = 0;
    let totTutte = 0, totPagate = 0, totDaPagare = 0, totScadute = 0;
    for (const f of fatture) {
      const imp = Number(f.importo) || 0;
      totTutte += imp;
      if (f.stato === 'pagata') { pagate++; totPagate += imp; }
      else if (isScaduta(f)) { scadute++; totScadute += imp; }
      else if (f.stato === 'da_pagare' || f.stato === 'parzialmente_pagata') { daPagare++; totDaPagare += imp; }
    }
    return {
      tutte: fatture.length, pagate, daPagare, scadute,
      totTutte, totPagate, totDaPagare, totScadute,
    };
  }, [fatture]);

  // Raggruppa fattureVisibili per mese (per la vista raggruppata).
  const gruppiMese = useMemo(() => {
    if (!raggruppaMese) return [];
    const map = new Map();
    for (const f of fattureVisibili) {
      const key = meseKeyOf(f);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === 'senza-data') return 1;
      if (b === 'senza-data') return -1;
      return b.localeCompare(a);
    });
    return keys.map(k => {
      const rows = map.get(k);
      const totale = rows.reduce((s, f) => {
        const imp = Number(f.importo) || 0;
        return f.tipo_documento === 'nota_credito' ? s - imp : s + imp;
      }, 0);
      return { key: k, label: meseLabel(k), rows, totale };
    });
  }, [raggruppaMese, fattureVisibili]);

  // Se la ricerca non trova nulla nel current view, controlla se esiste in
  // `tutteFatture` nella vista opposta (fattura vs nota_credito).
  const suggerimentoTipo = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q || fattureVisibili.length > 0 || !forceTipo) return null;
    // Usa lo STESSO filtro robusto del fetchFatture: opposto = tutto quello
    // che NON è nella vista corrente.
    const isNCView = forceTipo === 'nota_credito';
    const altrove = tutteFatture.filter(f => {
      // Escludi le fatture che SAREBBERO nella vista corrente
      const isNC = f.tipo_documento === 'nota_credito';
      if (isNCView ? isNC : !isNC) return false;
      // Coercizione a stringa DIFENSIVA: alcune fatture legacy hanno `numero` numerico,
      // e `.toLowerCase()` su un numero → TypeError → blue screen.
      const _s = (v) => String(v == null ? '' : v).toLowerCase();
      return (
        _s(f.numero).includes(q) ||
        _s(f.fornitore).includes(q) ||
        _s(f.protocollo).includes(q)
      );
    });
    if (altrove.length === 0) return null;
    const nomeAltrove = isNCView ? 'Fatture Ricevute' : 'Note di Credito Ricevute';
    const linkAltrove = isNCView
      ? '/admin/fatturazione/fatture-ricevute'
      : '/admin/fatturazione/note-credito-ricevute';
    return { count: altrove.length, nomeAltrove, linkAltrove };
  }, [searchQ, fattureVisibili, tutteFatture, forceTipo]);

  // iter100f: watchdog anti-blocco. Se `loading` resta true per > 20s
  // (fetch fermo, thread bloccato, JSON troppo grande), mostra un fallback
  // interattivo INVECE della schermata blu/vuota che aveva `fra.milone`.
  const [loadStuck, setLoadStuck] = useState(false);
  useEffect(() => {
    if (!loading) { setLoadStuck(false); return; }
    const t = setTimeout(() => setLoadStuck(true), 20000);
    return () => clearTimeout(t);
  }, [loading]);

  if (loading) {
    if (loadStuck) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-6" data-testid="fatture-load-stuck">
          <div className="max-w-md w-full bg-white border border-amber-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Caricamento più lento del previsto</h3>
                <p className="text-sm text-slate-600 mt-1">Il caricamento delle fatture sta impiegando più di 20 secondi. Può dipendere da un archivio molto grande o da connessione instabile.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => window.location.reload()} className="bg-slate-900 text-white">Ricarica la pagina</Button>
              <Button variant="outline" onClick={() => window.location.href = '/admin'}>Torna alla dashboard</Button>
            </div>
          </div>
        </div>
      );
    }
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  // iter84: renderer di riga estratto — usato sia dalla vista piatta sia da
  // quella raggruppata per mese. Coercizione a stringa DIFENSIVA su tutti i
  // campi user-facing per evitare crash su dati anomali.
  const numeroStr = (v) => String(v == null ? '' : v);
  const renderFatturaRow = (f) => (
    <TableRow key={f.id} className="border-slate-100" data-selected={bulkSel.isSelected(f.id) ? 'true' : 'false'}>
      <TableCell className="pl-3">
        <Checkbox
          checked={bulkSel.isSelected(f.id)}
          onCheckedChange={() => bulkSel.toggle(f.id)}
          aria-label={`Seleziona fattura ${numeroStr(f.numero)}`}
          data-testid={`bulk-select-fattura-entrata-${f.id}`}
        />
      </TableCell>
      <TableCell className="font-mono text-xs">
        <div className="flex items-center gap-1">
          {f.locked && <Lock className="h-3 w-3 text-slate-400" title="Bloccato (SDI)" />}
          <span className={f.protocollo ? 'text-slate-700' : 'text-slate-300'}>{numeroStr(f.protocollo) || '—'}</span>
        </div>
      </TableCell>
      <TableCell className="font-mono font-medium">
        <span className="break-all">{numeroStr(f.numero)}</span>
        {f.tipo_documento === 'nota_credito' && !forceTipo && <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">N. Credito</Badge>}
        {f.from_xml && <Badge variant="outline" className="ml-2 text-xs bg-purple-50 text-purple-700 border-purple-200">XML</Badge>}
      </TableCell>
      <TableCell className="text-slate-900">{numeroStr(f.fornitore)}</TableCell>
      <TableCell className="text-slate-600">{fmtData(f.data)}</TableCell>
      <TableCell>{getScadenzaBadge(f)}</TableCell>
      <TableCell className={`text-right font-medium ${f.tipo_documento === 'nota_credito' ? 'text-amber-700' : 'text-slate-900'}`}>
        <div className="flex flex-col items-end">
          <span>{f.tipo_documento === 'nota_credito' ? '-' : ''}{fmt(f.importo)}</span>
          {f.stato === 'parzialmente_pagata' && (Number(f.totale_pagato) || 0) > 0 && (
            <span className="text-[10px] text-blue-700 font-mono mt-0.5" data-testid={`parziale-progress-${f.id}`}>
              {fmt(f.totale_pagato)} / {fmt(f.importo)} €
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <select value={f.stato || 'da_pagare'} onChange={(e) => handleChangeStato(f.id, e.target.value)} className={`text-xs font-medium px-2 py-0.5 rounded-full border cursor-pointer ${statoSelectClass(f.stato)}`} data-testid={`stato-select-${f.id}`}>
            <option value="da_pagare">Da Pagare</option>
            <option value="parzialmente_pagata">Parzialmente pagata</option>
            <option value="pagata">Pagata</option>
            <option value="scaduta">Scaduta</option>
          </select>
          <div className="flex flex-wrap gap-1 mt-0.5">
            <Badge variant="outline" className={`text-[10px] py-0 px-1 ${f.stato_sdi === 'inviata' || f.stato_sdi === 'accettata' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>SDI: {numeroStr(f.stato_sdi) || 'non inv.'}</Badge>
            <Badge variant="outline" className={`text-[10px] py-0 px-1 ${f.stato_contabilizzazione === 'registrata' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>Cont: {f.stato_contabilizzazione === 'registrata' ? 'reg.' : 'no'}</Badge>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-0.5">
          {f.file_url && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => { setPreviewUrl(f.file_url); setPreviewTitle(`Doc ${numeroStr(f.numero)}`); setPreviewOpen(true); }} data-testid={`preview-fattura-entrata-${f.id}`} title="Anteprima allegato">
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {f.file_url && (
            <a href={getFileUrl(f.file_url)} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" title="Scarica"><Download className="h-4 w-4" /></Button>
            </a>
          )}
          {f.xml_items?.length > 0 && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => { setSelectedFattura(f); setDetailOpen(true); }} title="Dettaglio XML">
              <FileText className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" onClick={() => openLog(f)} data-testid={`log-fe-${f.id}`} title="Cronologia">
            <History className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => toggleContabilizza(f)} title="Contabilizza/Scontabilizza">
            <BookCheck className="h-4 w-4" />
          </Button>
          {!f.locked && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => lockSdi(f)} data-testid={`lock-sdi-${f.id}`} title="Invia a SDI e blocca">
              <Send className="h-4 w-4" />
            </Button>
          )}
          {f.locked && isSuper && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:bg-amber-50" onClick={() => unlockSdi(f)} data-testid={`unlock-sdi-${f.id}`} title="Sblocca per modificare">
              <Unlock className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-indigo-600 hover:bg-indigo-50" onClick={() => openPagamenti(f)} title="Gestisci pagamenti" data-testid={`pagamenti-fattura-${f.id}`}>
            <CreditCard className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:bg-amber-50 relative" onClick={() => openLinkCantiere(f)} title={f.cantiere_id ? `Cambia cantiere${f.acconto_riferimento ? ' · Acconto: ' + f.acconto_riferimento : ''}` : 'Collega a cantiere'} data-testid={`link-cantiere-fattura-${f.id}`}>
            <Building2 className="h-4 w-4" />
            {f.acconto_riferimento && <span className="absolute -top-1 -right-1 text-[9px] leading-none bg-amber-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-semibold pointer-events-none">{String(f.acconto_riferimento).startsWith('extra') ? 'E' : String(f.acconto_riferimento)}</span>}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 disabled:opacity-30" onClick={() => openEdit(f)} data-testid={`edit-fattura-${f.id}`} title={f.locked ? 'Bloccato (SDI)' : 'Modifica'} disabled={f.locked}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 disabled:opacity-30" onClick={() => handleDelete(f.id)} disabled={f.locked} title={f.locked ? 'Bloccato (SDI)' : 'Elimina'}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6" data-testid="fatture-entrata-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{title || (forceTipo === 'nota_credito' ? 'Note di Credito Ricevute' : 'Fatture in Entrata')}</h1>
          <p className="text-slate-500 mt-1">{subtitle || (forceTipo === 'nota_credito' ? 'Note di credito ricevute dai fornitori (riducono i costi e l\'IVA detraibile)' : 'Gestisci le fatture dei fornitori e le scadenze di pagamento')}</p>
        </div>
        <div className="flex gap-2">
          {!forceTipo || forceTipo === 'fattura' ? (
            <>
              <Button
                variant="outline"
                onClick={() => setBulkXmlOpen(true)}
                data-testid="import-xml-bulk-btn"
                title="Importa più fatture elettroniche XML in una sola operazione"
              >
                <Upload className="h-4 w-4 mr-2" />Importa XML in massa
              </Button>
              <label>
                <input type="file" accept=".xml,.html,.htm,.p7m" onChange={handleUploadXml} className="hidden" data-testid="import-xml-input" />
                <Button asChild variant="outline" className="cursor-pointer" disabled={uploading}>
                  <span><Upload className="h-4 w-4 mr-2" />{uploading ? 'Importazione...' : 'Importa XML singolo'}</span>
                </Button>
              </label>
              <Button variant="outline" onClick={checkAndDedupe} disabled={dedupeBusy} data-testid="dedupe-btn" title="Cerca ed elimina fatture importate più volte">
                <Copy className="h-4 w-4 mr-2" />{dedupeBusy ? 'Verifica…' : 'Verifica duplicati'}
              </Button>
            </>
          ) : null}
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={openNew} data-testid="add-fattura-btn">
            <Plus className="h-4 w-4 mr-2" />{forceTipo === 'nota_credito' ? 'Nuova Nota di Credito' : 'Nuova Fattura'}
          </Button>
        </div>
      </div>

      {/* iter81: Dialog import XML in massa.
          SafeTableBoundary opera sull'albero React (non sul DOM), quindi cattura
          anche un errore di render che avviene dentro il Dialog Radix, benché il
          suo contenuto sia portalato altrove nel DOM. */}
      <SafeTableBoundary label="dell'import fatture">
        <ImportXmlBulkDialog
          open={bulkXmlOpen}
          onClose={() => setBulkXmlOpen(false)}
          onImported={fetchFatture}
        />
      </SafeTableBoundary>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-red-200 bg-red-50"><CardContent className="p-4"><p className="text-xs uppercase text-red-600 font-medium">Da Pagare</p><p className="text-2xl font-bold text-red-700" data-testid="tot-da-pagare">{fmt(totDaPagare)}</p><p className="text-xs text-red-500">{totals.daPagareCount} fatture</p></CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4"><p className="text-xs uppercase text-emerald-600 font-medium">Pagate</p><p className="text-2xl font-bold text-emerald-700">{fmt(totPagate)}</p><p className="text-xs text-emerald-500">{totals.pagateCount} fatture</p></CardContent></Card>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <BulkActionToolbar
            resource="fatture_entrata"
            selectedIds={bulkSel.selectedIds}
            onClear={bulkSel.clear}
            onDeleted={fetchFatture}
            itemLabel="fattura"
          />
          {/* iter83-fix + iter84: toolbar con ricerca, filtro mese, raggruppamento */}
          <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Cerca per numero, fornitore, protocollo, descrizione…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="max-w-md flex-1 min-w-[200px]"
              data-testid="fatture-entrata-search"
            />
            <Select value={filtroMese} onValueChange={setFiltroMese}>
              <SelectTrigger className="w-[190px]" data-testid="fatture-entrata-filtro-mese">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i mesi</SelectItem>
                {mesiDisponibili.map(k => (
                  <SelectItem key={k} value={k}>{meseLabel(k)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant={raggruppaMese ? 'default' : 'outline'}
              onClick={() => setRaggruppaMese(v => !v)}
              className={raggruppaMese ? 'bg-slate-900 text-white' : ''}
              data-testid="fatture-entrata-toggle-raggruppa"
              title="Raggruppa fatture per mese con intestazioni collassabili"
            >
              {raggruppaMese ? <ListTree className="h-4 w-4 mr-1.5" /> : <LayoutList className="h-4 w-4 mr-1.5" />}
              {raggruppaMese ? 'Per mese' : 'Lista piatta'}
            </Button>
            {(searchQ || filtroMese !== 'tutti' || filtroStato !== 'tutte') && (
              <span className="text-xs text-slate-500" data-testid="fatture-entrata-search-count">
                {fattureVisibili.length} di {fatture.length} risultati
              </span>
            )}
            {(searchQ || filtroMese !== 'tutti' || filtroStato !== 'tutte') && (
              <Button size="sm" variant="ghost" onClick={() => { setSearchQ(''); setFiltroMese('tutti'); setFiltroStato('tutte'); }} data-testid="fatture-entrata-search-clear">
                Pulisci filtri
              </Button>
            )}
          </div>
          {/* iter86-q: filtri rapidi per stato pagamento */}
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-1.5" data-testid="fatture-entrata-filtri-stato">
            {[
              { key: 'tutte', label: 'Tutte', count: contatoriStato.tutte, tot: contatoriStato.totTutte, cls: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200', active: 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800' },
              { key: 'pagate', label: 'Pagate', count: contatoriStato.pagate, tot: contatoriStato.totPagate, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', active: 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700' },
              { key: 'da_pagare', label: 'Da pagare', count: contatoriStato.daPagare, tot: contatoriStato.totDaPagare, cls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100', active: 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700' },
              { key: 'scadute', label: 'Scadute', count: contatoriStato.scadute, tot: contatoriStato.totScadute, cls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', active: 'bg-red-600 text-white border-red-600 hover:bg-red-700' },
            ].map(f => {
              const isActive = filtroStato === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFiltroStato(f.key)}
                  className={`inline-flex flex-col items-start rounded-lg border px-3 py-1.5 transition-colors ${isActive ? f.active : f.cls}`}
                  data-testid={`fatture-entrata-filtro-stato-${f.key}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold">{f.label}</span>
                    <span className={`text-[10px] rounded-full px-1.5 py-0 ${isActive ? 'bg-white/20' : 'bg-white/70'}`}>{f.count}</span>
                  </div>
                  <span className={`text-[10px] mt-0.5 ${isActive ? 'opacity-90' : 'opacity-70'}`} data-testid={`fatture-entrata-filtro-tot-${f.key}`}>
                    € {(f.tot || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </button>
              );
            })}
          </div>
          {/* iter88: grafico Andamento mensile ultimi 12 mesi (Pagate vs Da pagare) */}
          <AndamentoMensileFatture
            fatture={fatture}
            testIdPrefix="fatture-entrata"
            accentColor="emerald"
            classifyStato={(f) => {
              if (!f || !f.data || typeof f.data !== 'string') return null;
              if (f.tipo_documento === 'nota_credito') return null;
              if (f.stato === 'annullata') return null;
              if (f.stato === 'pagata') return 'pagata';
              return 'da_pagare';
            }}
          />
          {suggerimentoTipo && (
            <div className="p-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900" data-testid="fatture-entrata-suggerimento">
              Nessun risultato in questa vista, ma <strong>{suggerimentoTipo.count} documento/i</strong> corrispondono alla ricerca in <strong>{suggerimentoTipo.nomeAltrove}</strong>.{' '}
              <a href={`${suggerimentoTipo.linkAltrove}?q=${encodeURIComponent(searchQ)}`} className="underline font-medium">Vai a {suggerimentoTipo.nomeAltrove} →</a>
            </div>
          )}
          <SafeTableBoundary label="delle fatture ricevute">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 pl-3">
                  <Checkbox
                    checked={bulkSel.isAllSelected}
                    onCheckedChange={bulkSel.toggleAll}
                    aria-label="Seleziona tutto"
                    data-testid="bulk-select-all-fatture-entrata"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Protocollo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">N. Fornitore</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fornitore</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Scadenza</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Importo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stati</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fattureVisibili.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">{(searchQ || filtroMese !== 'tutti' || filtroStato !== 'tutte') ? 'Nessuna fattura trovata con il filtro corrente' : (forceTipo === 'nota_credito' ? 'Nessuna nota di credito ricevuta' : 'Nessuna fattura in entrata')}</TableCell></TableRow>
              ) : raggruppaMese ? (
                gruppiMese.map(g => {
                  const isCollapsed = mesiCollassati.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      <TableRow className="bg-slate-100 hover:bg-slate-200 cursor-pointer" onClick={() => toggleMese(g.key)} data-testid={`gruppo-mese-${g.key}`}>
                        <TableCell colSpan={9} className="py-2">
                          <div className="flex items-center gap-2 text-slate-700 font-medium">
                            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            <span>{g.label}</span>
                            <span className="text-xs text-slate-500 font-normal">· {g.rows.length} fatture</span>
                            <span className="ml-auto text-sm font-mono text-slate-800">€ {fmt(g.totale)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      {!isCollapsed && g.rows.map(f => renderFatturaRow(f))}
                    </React.Fragment>
                  );
                })
              ) : (
                fattureVisibili.map(f => renderFatturaRow(f))
              )}
            </TableBody>
          </Table>
          </SafeTableBoundary>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica Fattura' : 'Nuova Fattura in Entrata'}</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 mt-4">
            {!forceTipo && (
              <div className="space-y-2">
                <Label>Tipo Documento</Label>
                <Select value={form.tipo_documento} onValueChange={v => { setForm({...form, tipo_documento: v, riferimento_fattura_id: ''}); }}>
                  <SelectTrigger data-testid="fe-tipo-documento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fattura">Fattura</SelectItem>
                    <SelectItem value="nota_credito">Nota di Credito (riduce la spesa)</SelectItem>
                  </SelectContent>
                </Select>
                {form.tipo_documento === 'nota_credito' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    La nota di credito viene contabilizzata come <strong>importo negativo</strong> nella contabilità del cantiere (riduce la spesa).
                  </p>
                )}
              </div>
            )}
            {form.tipo_documento === 'nota_credito' && (
              <div className="space-y-2">
                <Label>Fattura di riferimento *</Label>
                <Select value={form.riferimento_fattura_id} onValueChange={handleSelectRiferimento}>
                  <SelectTrigger data-testid="fe-riferimento"><SelectValue placeholder="Seleziona la fattura da stornare..." /></SelectTrigger>
                  <SelectContent>
                    {tutteFatture
                      .filter(f => f.tipo_documento !== 'nota_credito' && !['annullata','stornata'].includes(f.stato))
                      .map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.numero} · {f.fornitore} · € {(f.importo || 0).toFixed(2)}
                        </SelectItem>
                      ))
                    }
                    {tutteFatture.filter(f => f.tipo_documento !== 'nota_credito' && !['annullata','stornata'].includes(f.stato)).length === 0 && (
                      <div className="px-3 py-4 text-xs text-slate-400 text-center">
                        Nessuna fattura disponibile da stornare.
                      </div>
                    )}
                  </SelectContent>
                </Select>
                {form.riferimento_fattura_id && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                    La fattura selezionata verrà marcata come <strong>stornata</strong>. Resta visibile nel fatturato emesso ma viene rimossa da "Da Pagare".
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fornitore <span className="text-xs text-slate-400">(autocomplete da Anagrafica)</span></Label>
                <Input
                  list="dl-fornitori-fe"
                  value={form.fornitore}
                  onChange={(e) => {
                    const v = e.target.value;
                    const match = fornitoriList.find(f => f.ragione_sociale === v);
                    setForm(prev => ({
                      ...prev,
                      fornitore: v,
                      fornitore_piva: match?.partita_iva || prev.fornitore_piva || '',
                      fornitore_id: match?.id || '',
                    }));
                  }}
                  required
                  placeholder="Inizia a digitare o seleziona da anagrafica"
                  data-testid="fe-fornitore"
                />
                <datalist id="dl-fornitori-fe">
                  {fornitoriList.map(f => (
                    <option key={f.id} value={f.ragione_sociale}>{f.partita_iva ? `P.IVA ${f.partita_iva}` : ''}</option>
                  ))}
                </datalist>
                {form.fornitore_piva && (
                  <p className="text-[11px] text-emerald-700">P.IVA: <span className="font-mono">{form.fornitore_piva}</span></p>
                )}
              </div>
              <div className="space-y-2"><Label>N. Documento</Label><Input value={form.numero} onChange={(e) => setForm({...form, numero: e.target.value})} required placeholder={form.tipo_documento === 'nota_credito' ? 'NC-001' : 'FT-001'} data-testid="fe-numero" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Data Fattura</Label><Input type="date" value={form.data} onChange={(e) => {
                const v = e.target.value;
                setForm({...form, data: v});
                if (form.modalita_pagamento_id) setTimeout(() => recalcScadenza({ data: v }), 0);
              }} required data-testid="fe-data" /></div>
              <div className="space-y-2"><Label>Scadenza Pagamento</Label><Input type="date" value={form.scadenza} onChange={(e) => setForm({...form, scadenza: e.target.value})} required data-testid="fe-scadenza" /></div>
            </div>
            <div className="space-y-1 bg-blue-50/40 border border-blue-200 rounded-lg p-3">
              <Label className="text-sm font-semibold text-blue-900">Modalità di pagamento</Label>
              <Select
                value={form.modalita_pagamento_id || 'none'}
                onValueChange={(v) => {
                  const newId = v === 'none' ? '' : v;
                  if (newId) {
                    setForm(prev => ({...prev, modalita_pagamento_id: newId}));
                    setTimeout(() => recalcScadenza({ modalita_pagamento_id: newId }), 0);
                  } else {
                    setForm(prev => ({...prev, modalita_pagamento_id: '', pagamento_formula: ''}));
                  }
                }}
              >
                <SelectTrigger data-testid="fe-modalita-select"><SelectValue placeholder="Manuale (compila scadenza a mano)" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">Manuale (compila scadenza a mano)</SelectItem>
                  {modalitaList.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.pagamento_formula && (
                <p className="text-[11px] text-blue-700" data-testid="fe-formula-label">
                  Formula applicata: <span className="font-mono font-medium">{form.pagamento_formula}</span>
                </p>
              )}
              <p className="text-[11px] text-slate-500">La scadenza viene compilata in automatico in base alla formula scelta.</p>
            </div>
            <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm font-semibold">Importi (calcolo automatico)</Label>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-600">Modalità:</Label>
                  <Select value={form.input_mode} onValueChange={v => setForm(prev => recalc({...prev, input_mode: v}))}>
                    <SelectTrigger className="h-7 w-44 text-xs" data-testid="fe-input-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="imponibile">Inserisco imponibile</SelectItem>
                      <SelectItem value="totale">Inserisco totale (scorporo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Aliquota IVA (%)</Label>
                  <Select value={form.aliquota_iva} onValueChange={v => setForm(prev => recalc({...prev, aliquota_iva: v}))}>
                    <SelectTrigger className="h-9" data-testid="fe-aliquota"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (esente)</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="22">22%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.input_mode === 'imponibile' ? (
                  <div className="space-y-1">
                    <Label className="text-xs">Imponibile (netto) €</Label>
                    <Input type="number" step="0.01" value={form.imponibile} onChange={e => setForm(prev => recalc({...prev, imponibile: e.target.value}))} placeholder="0.00" required data-testid="fe-imponibile" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label className="text-xs">Totale (lordo IVA) €</Label>
                    <Input type="number" step="0.01" value={form.importo} onChange={e => setForm(prev => recalc({...prev, importo: e.target.value}))} placeholder="0.00" required data-testid="fe-importo" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
                <div className="text-center"><p className="text-[10px] uppercase text-slate-500">Imponibile</p><p className="font-mono text-sm font-semibold text-slate-900">€ {form.imponibile || '0.00'}</p></div>
                <div className="text-center"><p className="text-[10px] uppercase text-slate-500">IVA {form.aliquota_iva}%</p><p className="font-mono text-sm font-semibold text-slate-900">€ {form.iva || '0.00'}</p></div>
                <div className="text-center bg-purple-50 rounded px-1"><p className="text-[10px] uppercase text-purple-600">Totale</p><p className="font-mono text-sm font-bold text-purple-700">€ {form.importo || '0.00'}</p></div>
              </div>
            </div>
            <div className="space-y-2"><Label>Descrizione</Label><Input value={form.descrizione} onChange={(e) => setForm({...form, descrizione: e.target.value})} placeholder="Materiale elettrico" /></div>
            <div className="space-y-2">
              <Label>Cantiere (opzionale)</Label>
              <CantiereSelectByCliente
                cantieri={cantieri}
                value={form.cantiere_id}
                onChange={v => { setForm({...form, cantiere_id: v, acconto_riferimento: v ? form.acconto_riferimento : ''}); }}
                placeholder="Collega a cantiere — raggruppato per cliente"
                dataTestId="fe-cantiere"
              />
              {form.cantiere_id && (
                <div className="pt-2">
                  <AccontoSelector
                    value={form.acconto_riferimento}
                    onChange={v => setForm({...form, acconto_riferimento: v})}
                    label="Riferimento acconto (opzionale)"
                    hint="Assegna la fattura a un acconto del cantiere oppure segnalala come Extra (fuori preventivo). Lascia vuoto per non collegarla ad alcun acconto."
                    dataTestId="fattura-entrata-acconto"
                  />
                  {form.acconto_riferimento && (
                    <button
                      type="button"
                      className="text-xs text-slate-500 underline hover:text-slate-700 mt-1"
                      onClick={() => setForm({...form, acconto_riferimento: ''})}
                      data-testid="fattura-entrata-acconto-clear"
                    >Rimuovi riferimento acconto</button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2 border-t pt-3">
              <Label>Allegato (PDF/immagine — opzionale)</Label>
              {form.file_url ? (
                <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                    <span className="text-sm text-blue-900 truncate">File allegato</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={getFileUrl(form.file_url)} target="_blank" rel="noopener noreferrer">
                      <Button type="button" variant="ghost" size="sm" className="h-7" data-testid="fe-view-file"><Eye className="h-3.5 w-3.5" /></Button>
                    </a>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => setForm(prev => ({...prev, file_url: ''}))} data-testid="fe-remove-file">Rimuovi</Button>
                  </div>
                </div>
              ) : (
                <Input
                  type="file"
                  accept="application/pdf,image/*,.xml,.p7m"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  data-testid="fe-file-input"
                />
              )}
              {uploadingFile && <p className="text-xs text-slate-500">Caricamento in corso...</p>}
            </div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button><Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-fattura-entrata">Salva</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog for XML fatture */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dettaglio Fattura {selectedFattura?.numero}</DialogTitle></DialogHeader>
          {selectedFattura && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Fornitore</p><p className="font-medium">{selectedFattura.fornitore}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Numero</p><p className="font-medium">{selectedFattura.numero}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Data</p><p className="font-medium">{selectedFattura.data}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Scadenza</p><p className="font-medium">{selectedFattura.scadenza}</p></div>
              </div>
              {selectedFattura.xml_items?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Dettaglio Voci</p>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Descrizione</TableHead>
                      <TableHead className="text-xs text-right">Qta</TableHead>
                      <TableHead className="text-xs text-right">Prezzo Unit.</TableHead>
                      <TableHead className="text-xs text-right">Totale</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {selectedFattura.xml_items.map((item, idx) => (
                        <TableRow key={`xml-${item.descrizione || ''}-${idx}`}><TableCell className="text-sm">{item.descrizione || '-'}</TableCell><TableCell className="text-right text-sm">{item.quantita || '-'}</TableCell><TableCell className="text-right text-sm">{fmt(item.prezzo_unitario)}</TableCell><TableCell className="text-right font-medium text-sm">{fmt(item.prezzo_totale)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                <div className="p-3 bg-slate-50 rounded-lg text-center"><p className="text-xs text-slate-500">Imponibile</p><p className="font-bold">{fmt(selectedFattura.imponibile)}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg text-center"><p className="text-xs text-slate-500">IVA</p><p className="font-bold">{fmt(selectedFattura.iva)}</p></div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center"><p className="text-xs text-purple-600">Totale</p><p className="font-bold text-purple-700 text-lg">{fmt(selectedFattura.importo)}</p></div>
              </div>
              {/* Scadenze Pagamento */}
              {selectedFattura.scadenze_pagamento?.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-2">Scadenze Pagamento {selectedFattura.condizioni_pagamento === 'TP01' ? '(a rate)' : ''}</p>
                  <div className="space-y-2">
                    {selectedFattura.scadenze_pagamento.map((s, idx) => (
                      <div key={`${s.scadenza || 'nd'}-${s.importo || 0}-${idx}`} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200">
                        <div>
                          <span className="text-sm font-medium">{s.scadenza || 'N/D'}</span>
                          {s.iban && <span className="text-xs text-slate-400 ml-2">IBAN: {s.iban}</span>}
                        </div>
                        <span className="font-medium text-sm">{fmt(s.importo)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedFattura.xml_filename && <p className="text-xs text-slate-400 pt-2">File: {selectedFattura.xml_filename}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh]">
          <DialogHeader><DialogTitle>{previewTitle}</DialogTitle></DialogHeader>
          <div className="mt-4 w-full" style={{ height: '70vh' }}>
            <FilePreview url={previewUrl} title={previewTitle} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Cronologia Dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              <History className="h-4 w-4 inline mr-2" />
              Cronologia documento
              {logDoc && <span className="ml-2 text-xs font-mono text-slate-500">{logDoc.protocollo} · N. {logDoc.numero}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-96 overflow-y-auto">
            {logEntries.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Nessuna voce di log</p>
            ) : logEntries.slice().reverse().map((e, i) => (
              <div key={`${e.timestamp || ''}-${e.action || ''}-${i}`} className="border border-slate-200 rounded p-3 bg-slate-50">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <Badge variant="outline" className="bg-white text-xs">{e.action}</Badge>
                  <span className="text-xs text-slate-500">{e.timestamp ? format(parseISO(e.timestamp), 'dd-MM-yyyy HH:mm:ss') : ''}</span>
                </div>
                <p className="text-sm text-slate-700 mt-1">{e.details}</p>
                <p className="text-xs text-slate-400 mt-1">da {e.user_name}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pagamenti dialog (iter47) */}
      {pagamentiFattura && (
        <PagamentiDialog
          open={pagamentiOpen}
          onOpenChange={(v) => { setPagamentiOpen(v); if (!v) setPagamentiFattura(null); }}
          fattura={pagamentiFattura}
          tipo="fatture-entrata"
          onUpdated={fetchFatture}
        />
      )}

      {/* iter85: Link cantiere anche con SDI-lock */}
      <Dialog open={!!linkCantiereFattura} onOpenChange={(v) => { if (!v) setLinkCantiereFattura(null); }}>
        <DialogContent className="max-w-md" data-testid="link-cantiere-dialog">
          <DialogHeader>
            <DialogTitle>Collega fattura a cantiere</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              <div><span className="text-slate-400">Fornitore:</span> <strong>{linkCantiereFattura?.fornitore}</strong></div>
              <div><span className="text-slate-400">Numero:</span> <span className="font-mono">{linkCantiereFattura?.numero}</span></div>
              {linkCantiereFattura?.locked && (
                <div className="mt-2 flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                  <Lock className="h-3 w-3" />
                  Fattura bloccata SDI: puoi collegare il cantiere ma i dati fiscali restano immutabili.
                </div>
              )}
            </div>
            <div>
              <Label>Cantiere</Label>
              <Select value={linkCantiereValue || '__none__'} onValueChange={v => setLinkCantiereValue(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="link-cantiere-select"><SelectValue placeholder="Nessun cantiere" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessun cantiere —</SelectItem>
                  {cantieri.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.cliente_nome ? `· ${c.cliente_nome}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {linkCantiereValue && (
              <div>
                <AccontoSelector
                  value={linkAccontoValue}
                  onChange={setLinkAccontoValue}
                  label="Riferimento acconto (opzionale)"
                  hint="Assegna la fattura a un acconto del cantiere oppure segnalala come Extra (fuori preventivo). Lascia vuoto per non collegarla ad alcun acconto."
                  dataTestId="link-cantiere-acconto"
                />
                {linkAccontoValue && (
                  <button
                    type="button"
                    className="mt-1 text-[11px] text-slate-500 hover:text-slate-700 underline"
                    onClick={() => setLinkAccontoValue('')}
                    data-testid="link-cantiere-acconto-clear"
                  >Rimuovi riferimento acconto</button>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setLinkCantiereFattura(null)}>Annulla</Button>
              <Button onClick={saveLinkCantiere} data-testid="link-cantiere-save-btn">Salva</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
